// The appearance control as a top-right ICON BUTTON + dropdown (Brief 4 polish).
// Replaces the segmented toggle in the sidebar footer: the trigger is icon-only
// (the current appearance's glyph), and the dropdown lists System / Light / Dark
// / High contrast with the active one checked. Drives the same `useAppearance`
// hook (applies + persists + cross-surface syncs), so picking an option still
// SNAPS the whole surface. Token-pure (var(--waytide-sys-*)).

import { useRef, useState } from "react";
import { Contrast, Monitor, Moon, Sun, Check } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Appearance } from "@waytide/core";
import { useAppearance } from "./useAppearance";
import { useOutsideClose } from "../shell/useOutsideClose";

const OPTIONS: { value: Appearance; label: string; Icon: LucideIcon }[] = [
  { value: "system", label: "System", Icon: Monitor },
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "high-contrast", label: "High contrast", Icon: Contrast },
];

export function AppearanceMenu() {
  const { appearance, setAppearance } = useAppearance();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClose(open, ref, () => setOpen(false));

  const ActiveIcon =
    OPTIONS.find((o) => o.value === appearance)?.Icon ?? Monitor;

  return (
    <div className="wt-topbar__menu" ref={ref}>
      <button
        type="button"
        className="wt-topbar__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Appearance"
        data-testid="appearance-menu-trigger"
        onClick={() => setOpen((o) => !o)}
      >
        <ActiveIcon size={18} aria-hidden="true" />
      </button>
      {open ? (
        <div className="wt-topbar__pop" role="menu" aria-label="Appearance">
          {OPTIONS.map(({ value, label, Icon }) => {
            const active = value === appearance;
            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                className="wt-topbar__item"
                data-active={active ? "true" : "false"}
                data-testid={`appearance-opt-${value}`}
                onClick={() => {
                  setAppearance(value);
                  setOpen(false);
                }}
              >
                <Icon size={16} aria-hidden="true" className="wt-topbar__item-icon" />
                <span className="wt-topbar__item-label">{label}</span>
                {active ? (
                  <Check size={15} aria-hidden="true" className="wt-topbar__item-check" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
